import React from 'react';
import { SelectFieldComponent } from '../common/SelectFieldComponent';
import { useTiposIdentificacion } from '../../hooks/useParametric';
import { type TipoIdentificacion } from '../../types/catalog';

export interface TipoIdentificacionSelectProps {
  /** Current selected value */
  value?: number | string;
  /** Change handler */
  onChange: (value: string | number) => void;
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Custom filter function to filter tipos de identificación */
  filter?: (tipoIdentificacion: TipoIdentificacion) => boolean;
  /** Custom sx prop for styling */
  sx?: any;
}

/**
 * TipoIdentificacionSelect Component
 *
 * A reusable dropdown component that displays tipos de identificación from parametric data.
 * Features:
 * - Automatic data loading from parametric store
 * - Loading states and error handling
 * - Filtering capabilities
 * - Full TypeScript support
 * - Consistent styling with other form components
 */
export const TipoIdentificacionSelect: React.FC<TipoIdentificacionSelectProps> = ({
  value,
  onChange,
  label = 'Tipo de Identificación',
  error,
  disabled = false,
  required = false,
  placeholder,
  filter,
  sx,
}) => {
  const { data: tiposIdentificacion, loading, fetch: fetchTiposIdentificacion } = useTiposIdentificacion();

  // Fetch tipos de identificación on component mount
  React.useEffect(() => {
    fetchTiposIdentificacion();
  }, [fetchTiposIdentificacion]);

  // Filter tipos de identificación based on props
  const filteredTiposIdentificacion = React.useMemo(() => {
    let filtered = tiposIdentificacion;

    // Apply custom filter if provided
    if (filter) {
      filtered = filtered.filter(filter);
    }

    return filtered;
  }, [tiposIdentificacion, filter]);

  // Convert tipos de identificación to select options
  const options = React.useMemo(() => {
    return filteredTiposIdentificacion.map((tipoIdentificacion) => ({
      value: tipoIdentificacion.idTipoIdentificacion,
      label: tipoIdentificacion.tipo,
    }));
  }, [filteredTiposIdentificacion]);

  // Handle error state
  const displayError = error;

  return (
    <SelectFieldComponent
      label={required ? `${label} *` : label}
      value={value || ''}
      onChange={onChange}
      options={options}
      error={displayError}
      disabled={disabled || loading}
      placeholder={placeholder}
      loading={loading}
      sx={sx}
    />
  );
};

export default TipoIdentificacionSelect;